import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ImportDialog } from '../ui/ImportDialog';
import { ExportDialog } from '../ui/ExportDialog';
import { DataMenu } from '../ui/DataMenu';
import { GoogleDriveSaveDialog } from '../ui/GoogleDriveSaveDialog';
import { GoogleDriveLoadDialog } from '../ui/GoogleDriveLoadDialog';
import { isGoogleDriveConfigured } from '../../config/googleDrive';

interface LayoutProps {
  children: ReactNode;
}

interface Toast {
  message: string;
  type: 'success' | 'error';
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [toast, setToast] = useState<Toast | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isDriveSaveOpen, setIsDriveSaveOpen] = useState(false);
  const [isDriveLoadOpen, setIsDriveLoadOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const driveConfigured = isGoogleDriveConfigured();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
      setTimeout(() => setToast(null), 3000);
    },
    []
  );

  const handleOpenExportDialog = useCallback(() => {
    setIsExportDialogOpen(true);
  }, []);

  const handleCloseExportDialog = useCallback(() => {
    setIsExportDialogOpen(false);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const handleOpenImportDialog = useCallback(() => {
    setIsImportDialogOpen(true);
  }, []);

  const handleCloseImportDialog = useCallback(() => {
    setIsImportDialogOpen(false);
  }, []);

  const handleImportSuccess = useCallback(
    (message: string) => {
      showToast(message, 'success');
    },
    [showToast]
  );

  const handleImportError = useCallback(
    (message: string) => {
      showToast(message, 'error');
    },
    [showToast]
  );

  const handleOpenDriveSave = useCallback(() => {
    setIsDriveSaveOpen(true);
  }, []);

  const handleOpenDriveLoad = useCallback(() => {
    setIsDriveLoadOpen(true);
  }, []);

  return (
    <div className="min-h-screen bg-near-white text-near-black flex flex-col">
      <header className="bg-brand-primary text-white p-4">
        <nav className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="text-xl font-bold hover:opacity-80 transition"
            >
              Budgeter
            </Link>
            <Link
              to="/transacciones"
              className={`hidden sm:block hover:opacity-80 transition ${
                isActive('/transacciones') ? 'font-semibold underline' : ''
              }`}
            >
              Transacciones
            </Link>
            <Link
              to="/presupuesto"
              className={`hidden sm:block hover:opacity-80 transition ${isActive('/presupuesto') ? 'font-semibold underline' : ''}`}
            >
              Presupuesto
            </Link>
            <Link
              to="/reportes"
              className={`hidden sm:block hover:opacity-80 transition ${isActive('/reportes') ? 'font-semibold underline' : ''}`}
            >
              Reportes
            </Link>
            <Link
              to="/ajustes"
              className={`hidden sm:block hover:opacity-80 transition ${
                isActive('/ajustes') ? 'font-semibold underline' : ''
              }`}
            >
              Ajustes
            </Link>
          </div>
          <div className="hidden sm:flex gap-4 items-center">
            <DataMenu
              onImportLocal={handleOpenImportDialog}
              onExportLocal={handleOpenExportDialog}
              onDriveSave={handleOpenDriveSave}
              onDriveLoad={handleOpenDriveLoad}
            />
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="sm:hidden p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:opacity-80 transition"
            aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </nav>
        {isMobileMenuOpen && (
          <div className="sm:hidden mt-4 pb-2 border-t border-white/20 pt-4">
            <div className="flex flex-col gap-2">
              <Link
                to="/"
                className={`px-4 py-3 min-h-[44px] rounded hover:bg-white/10 transition ${
                  isActive('/') ? 'bg-white/10 font-semibold' : ''
                }`}
              >
                🏠 Inicio
              </Link>
              <Link
                to="/transacciones"
                className={`px-4 py-3 min-h-[44px] rounded hover:bg-white/10 transition ${
                  isActive('/transacciones') ? 'bg-white/10 font-semibold' : ''
                }`}
              >
                📋 Transacciones
              </Link>
              <Link
                to="/presupuesto"
                className={`px-4 py-3 min-h-[44px] rounded hover:bg-white/10 transition ${
                  isActive('/presupuesto') ? 'bg-white/10 font-semibold' : ''
                }`}
              >
                💰 Presupuesto
              </Link>
              <Link
                to="/reportes"
                className={`px-4 py-3 min-h-[44px] rounded hover:bg-white/10 transition ${
                  isActive('/reportes') ? 'bg-white/10 font-semibold' : ''
                }`}
              >
                📊 Reportes
              </Link>
              <Link
                to="/ajustes"
                className={`px-4 py-3 min-h-[44px] rounded hover:bg-white/10 transition ${
                  isActive('/ajustes') ? 'bg-white/10 font-semibold' : ''
                }`}
              >
                ⚙️ Ajustes
              </Link>
              <div className="border-t border-white/20 my-2" />
              <button
                onClick={() => {
                  handleOpenImportDialog();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 min-h-[44px] text-left rounded hover:bg-white/10 transition"
              >
                📄 Importar datos
              </button>
              <button
                onClick={() => {
                  handleOpenExportDialog();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 min-h-[44px] text-left rounded hover:bg-white/10 transition"
              >
                💾 Exportar datos
              </button>
              {driveConfigured && (
                <>
                  <button
                    onClick={() => {
                      handleOpenDriveSave();
                      setIsMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 min-h-[44px] text-left rounded hover:bg-white/10 transition"
                  >
                    ☁️ Guardar en Drive
                  </button>
                  <button
                    onClick={() => {
                      handleOpenDriveLoad();
                      setIsMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 min-h-[44px] text-left rounded hover:bg-white/10 transition"
                  >
                    📥 Cargar desde Drive
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>
      {toast && (
        <div
          className={`fixed top-20 right-4 px-4 py-2 rounded shadow-lg z-50 ${
            toast.type === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
      <ImportDialog
        isOpen={isImportDialogOpen}
        onClose={handleCloseImportDialog}
        onSuccess={handleImportSuccess}
        onError={handleImportError}
      />
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={handleCloseExportDialog}
        onSuccess={(message) => showToast(message, 'success')}
        onError={(message) => showToast(message, 'error')}
      />
      {driveConfigured && (
        <GoogleDriveSaveDialog
          isOpen={isDriveSaveOpen}
          onClose={() => setIsDriveSaveOpen(false)}
          onSuccess={(message) => showToast(message, 'success')}
          onError={(message) => showToast(message, 'error')}
        />
      )}
      {driveConfigured && (
        <GoogleDriveLoadDialog
          isOpen={isDriveLoadOpen}
          onClose={() => setIsDriveLoadOpen(false)}
          onSuccess={(message) => showToast(message, 'success')}
          onError={(message) => showToast(message, 'error')}
        />
      )}
      <main className="flex-1">{children}</main>
      <footer className="bg-near-black text-light-gray p-4">
        <div className="max-w-7xl mx-auto text-center text-sm">
          <p>Budgeter - Finanzas personales en tu dispositivo</p>
        </div>
      </footer>
    </div>
  );
}
